"use client";
import { useEffect, useState } from "react";
import type { CardRecord } from "@/lib/posts-helpers";
import { StickerGrid } from "@/components/stickers/StickerGrid";
import { LoadMoreClient, type LoadMoreFetcher } from "@/components/stickers/LoadMoreClient";
import { COUNTRY_CHANGE_EVENT, DEFAULT_COUNTRY, readCountryCookie } from "@/lib/country";

interface LocalizedFeedProps {
  /** Page-1 records the server rendered, for DEFAULT_COUNTRY. */
  initialRecords: CardRecord[];
  initialHasNext: boolean;
  initialPage: number;
  /** How many records go in the SSR'd grid; the rest are handed to LoadMoreClient. */
  initialVisible: number;
  /** Server action, already bound to this feed. Reads the country cookie itself. */
  fetcher: LoadMoreFetcher;
  /**
   * Only feed-kind routes vary by country. Hashtag-group categories (memes,
   * love, …) query a fixed tag list and are identical everywhere, so they skip
   * the swap entirely rather than paying for a pointless round trip.
   */
  localize: boolean;
  /** Server-rendered ad slot placed between the grid and the load-more region. */
  adSlot: React.ReactNode;
}

/**
 * Renders the feed grid, and replaces it with the visitor's own country's feed
 * after hydration when that differs from the default.
 *
 * Why this exists: `CategoryRoute` used to resolve the country with
 * `cookies()` during render. A dynamic API anywhere in the tree opts the route
 * out of static generation, so `/` and the five feed categories answered
 * `cache-control: private, no-cache, no-store` on every request — no CDN
 * caching at all, for the highest-traffic pages on the site.
 *
 * The server now renders DEFAULT_COUNTRY, which is exactly the response a
 * visitor with no cookie has always received, so the cached HTML is a real
 * page rather than a compromise — and it is what crawlers get, consistently.
 * This component then asks the server action for the visitor's own page 1,
 * which does read the cookie (a server action is a POST, never prerendered).
 *
 * Note this replaces the old `window.location.reload()` the country resolver
 * used to do: a full document reload to pick up a cookie the server no longer
 * reads would have been pure waste.
 */
export function LocalizedFeed({
  initialRecords,
  initialHasNext,
  initialPage,
  initialVisible,
  fetcher,
  localize,
  adSlot,
}: LocalizedFeedProps) {
  const [records, setRecords] = useState(initialRecords);
  const [hasNext, setHasNext] = useState(initialHasNext);
  const [localized, setLocalized] = useState(false);

  useEffect(() => {
    if (!localize) return;
    let cancelled = false;
    let requested = false;

    function swapIfLocalised() {
      const country = readCountryCookie();
      // No cookie, or the default — the server render is already correct.
      if (!country || country === DEFAULT_COUNTRY) return;
      // The cookie can be written and announced in the same tick it is first
      // read; only ever fetch once per mount.
      if (requested) return;
      requested = true;

      fetcher(initialPage)
        .then((result) => {
          // An empty result means the country has no feed of its own; keep the
          // server render rather than blanking the page.
          if (cancelled || !result.records?.length) return;
          setRecords(result.records);
          setHasNext(result.hasNext);
          setLocalized(true);
        })
        .catch(() => {
          // Localisation is an enhancement. On failure the visitor keeps the
          // default feed, which is a complete, working page.
        });
    }

    // The cookie is usually already there on a repeat visit…
    swapIfLocalised();
    // …but on a first visit CountryProbe is still waiting on /api/country when
    // this mounts, so wait for it to announce the country rather than leaving
    // the visitor on the default feed until their next navigation.
    window.addEventListener(COUNTRY_CHANGE_EVENT, swapIfLocalised);

    return () => {
      cancelled = true;
      window.removeEventListener(COUNTRY_CHANGE_EVENT, swapIfLocalised);
    };
  }, [localize, fetcher, initialPage]);

  return (
    <>
      <StickerGrid records={records.slice(0, initialVisible)} withInGridAds startIndex={0} />
      {adSlot}
      <LoadMoreClient
        // Remounts on swap so paging state, the appended list and the ad
        // cadence counter all reset to the new feed instead of interleaving
        // two countries' records.
        key={localized ? "localized" : "default"}
        initialPage={initialPage}
        initialHasNext={hasNext}
        fetcher={fetcher}
        withInGridAds
        initialRecordCount={Math.min(records.length, initialVisible)}
        prefetched={records.slice(initialVisible)}
      />
    </>
  );
}
